"use client"

import { useState, useEffect } from "react"
import { DashboardLayout } from "@/components/dashboard/dashboard-layout"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { TabGroup, TabGroupList, TabGroupTrigger } from "@/components/ui/tab-group"
import { Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, RefreshCw, FileSearch } from "lucide-react"
import { CustomDateRangeDialog } from "@/components/shared/custom-daterange-dialog"
import { ApiLogDetailModal } from "@/components/shared/ApiLogDetailModal"
import type { DateRange } from "react-day-picker"
import { listApiLogs, type ApiLogItem } from "@/lib/api/client-api-logs"
import { toast } from "sonner"
import { format } from "date-fns"
import { getBankName } from "@/lib/utils/bank-names"

export default function ApiHistoryPage() {
  const [isCustomDateRangeOpen, setIsCustomDateRangeOpen] = useState(false)
  const [logs, setLogs] = useState<ApiLogItem[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState("")
  const [appliedSearchTerm, setAppliedSearchTerm] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [selectedTimeRange, setSelectedTimeRange] = useState("today")
  const [dateRange, setDateRange] = useState<DateRange | undefined>()

  // Detail modal state
  const [selectedLogId, setSelectedLogId] = useState<string | null>(null)
  const [isDetailOpen, setIsDetailOpen] = useState(false)

  const itemsPerPage = 20

  useEffect(() => {
    loadLogs()
  }, [currentPage, selectedTimeRange, dateRange, appliedSearchTerm])

  const loadLogs = async () => {
    try {
      setLoading(true)

      const filters: Parameters<typeof listApiLogs>[0] = {
        page: currentPage,
        limit: itemsPerPage,
      }

      if (appliedSearchTerm) {
        filters.endpoint = appliedSearchTerm
      }

      if (selectedTimeRange === "today") {
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        filters.startDate = today.toISOString()
      } else if (selectedTimeRange === "week") {
        const weekAgo = new Date()
        weekAgo.setDate(weekAgo.getDate() - 7)
        filters.startDate = weekAgo.toISOString()
      } else if (selectedTimeRange === "month") {
        const monthAgo = new Date()
        monthAgo.setMonth(monthAgo.getMonth() - 1)
        filters.startDate = monthAgo.toISOString()
      } else if (dateRange?.from) {
        filters.startDate = dateRange.from.toISOString()
        if (dateRange.to) {
          filters.endDate = dateRange.to.toISOString()
        }
      }

      const response = await listApiLogs(filters)

      if (response.success) {
        setLogs(response.data.logs)
        setTotalPages(response.data.pagination.totalPages)
        setTotalItems(response.data.pagination.totalItems)
      }
    } catch (error: any) {
      toast.error(error.message || "ไม่สามารถโหลดข้อมูล API logs ได้")
      setLogs([])
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = () => {
    const normalized = searchTerm.trim()

    if (currentPage !== 1) {
      setCurrentPage(1)
    }

    if (appliedSearchTerm !== normalized) {
      setAppliedSearchTerm(normalized)
      return
    }

    // Re-fetch when searching with the same term on the same page.
    loadLogs()
  }

  const handleCustomDateRangeConfirm = (newDateRange: DateRange | undefined) => {
    setDateRange(newDateRange)
    setSelectedTimeRange("custom")
    setCurrentPage(1)
  }

  const handleTimeRangeChange = (range: string) => {
    setSelectedTimeRange(range)
    setDateRange(undefined)
    setCurrentPage(1)
  }

  const handleViewDetail = (logId: string) => {
    setSelectedLogId(logId)
    setIsDetailOpen(true)
  }

  const getStatusBadge = (status: number | null) => {
    if (!status) return <Badge variant="secondary">-</Badge>
    if (status >= 200 && status < 300) return <Badge className="bg-green-500 text-white">Success {status}</Badge>
    if (status >= 400 && status < 500) return <Badge className="bg-yellow-500 text-white">Error {status}</Badge>
    if (status >= 500) return <Badge className="bg-red-500 text-white">Server {status}</Badge>
    return <Badge variant="secondary">{status}</Badge>
  }

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-'
    try {
      const d = new Date(dateString)
      if (isNaN(d.getTime())) return '-'
      return format(d, 'dd/MM/yyyy HH:mm:ss')
    } catch {
      return dateString
    }
  }

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page)
    }
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Page Title */}
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-foreground">ประวัติการใช้งาน API Connect</h1>
            <p className="text-sm text-muted-foreground">บันทึก request / response จากทุก API call</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={loadLogs}
            disabled={loading}
            className="gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Search and Filter Section */}
        <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4 justify-between">
          {/* Search Input and Button */}
          <div className="flex gap-2 flex-1 max-w-md">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input 
                type="text" 
                placeholder="ค้นหา Endpoint path" 
                className="pl-10 h-11"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <Button 
              variant="secondary" 
              className="bg-gray-400 hover:bg-gray-500 text-white px-6 h-11"
              onClick={handleSearch}
              disabled={loading}
            >
              ค้นหา
            </Button>
          </div>

          {/* Date Filter Buttons */}
          <TabGroup value={selectedTimeRange} onValueChange={handleTimeRangeChange} size="md" color="brand-light-red">
            <TabGroupList>
              <TabGroupTrigger value="today">วันนี้</TabGroupTrigger>
              <TabGroupTrigger value="week">สัปดาห์นี้</TabGroupTrigger>
              <TabGroupTrigger value="month">เดือนนี้</TabGroupTrigger>
              <button
                type="button"
                className={`px-4 py-2 font-medium rounded-lg transition-colors ${
                  selectedTimeRange === "custom" 
                    ? "bg-brand-light-red/20 text-brand-light-red" 
                    : "text-gray-700 hover:bg-brand-light-red/10"
                }`}
                onClick={() => setIsCustomDateRangeOpen(true)}
              >
                กำหนดเอง
              </button>
            </TabGroupList>
          </TabGroup>
        </div>

        {/* Stats Summary */}
        <div className="bg-card rounded-lg border border-border p-4">
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>Total API Requests: <span className="font-semibold text-foreground">{totalItems.toLocaleString()}</span></span>
            <span>Page {currentPage} of {totalPages}</span>
          </div>
        </div>

        {/* Table */}
        <div className="bg-card rounded-lg border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="text-left p-4 font-medium text-foreground">API Endpoint</th>
                  <th className="text-left p-4 font-medium text-foreground">API Bank</th>
                  <th className="text-left p-4 font-medium text-foreground">วันที่</th>
                  <th className="text-left p-4 font-medium text-foreground">Status</th>
                  <th className="text-left p-4 font-medium text-foreground">Response Time</th>
                  <th className="text-left p-4 font-medium text-foreground">Error</th>
                  <th className="text-center p-4 font-medium text-foreground">รายละเอียด</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="p-8 text-center text-muted-foreground">
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                      Loading...
                    </td>
                  </tr>
                ) : logs.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="p-8 text-center text-muted-foreground">
                      ไม่พบข้อมูล API logs
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => (
                    <tr key={log.id} className="border-b border-border hover:bg-muted/30 transition-colors">
                      {/* Endpoint */}
                      <td className="p-4">
                        <div className="font-mono text-sm">
                          <span className="font-semibold text-blue-600">{log.method}</span>{' '}
                          <span className="text-foreground">{log.endpoint}</span>
                        </div>
                        {log.ipAddress && (
                          <div className="text-xs text-muted-foreground mt-0.5 font-mono">{log.ipAddress}</div>
                        )}
                        {log.verificationId && (
                          <div className="text-xs text-muted-foreground mt-0.5">
                            Verify: <span className="font-mono">{log.verificationId}</span>
                          </div>
                        )}
                        {log.generationId && (
                          <div className="text-xs text-muted-foreground mt-0.5">
                            Gen: <span className="font-mono">{log.generationId}</span>
                          </div>
                        )}
                      </td>
                      {/* API Bank */}
                      <td className="p-4 text-sm text-foreground whitespace-nowrap">
                        {getBankName(log.apiBank)}
                      </td>
                      {/* Date */}
                      <td className="p-4 text-sm text-muted-foreground whitespace-nowrap">
                        {formatDate(log.createdAt)}
                      </td>
                      {/* Status */}
                      <td className="p-4">
                        {getStatusBadge(log.status)}
                      </td>
                      {/* Response time */}
                      <td className="p-4 text-sm text-muted-foreground">
                        {log.processingTimeMs !== null ? `${log.processingTimeMs}ms` : '-'}
                      </td>
                      {/* Error — only show when HTTP status >= 400 */}
                      <td className="p-4 text-sm">
                        {log.errorCode && log.status !== null && log.status >= 400
                          ? <span className="text-red-500 font-mono text-xs">{log.errorCode}</span>
                          : <span className="text-muted-foreground">-</span>}
                      </td>
                      {/* Detail button */}
                      <td className="p-4 text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="gap-1.5 text-xs"
                          onClick={() => handleViewDetail(log.id)}
                        >
                          <FileSearch className="h-3.5 w-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 py-4 border-t">
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8"
                onClick={() => goToPage(1)}
                disabled={currentPage === 1}
              >
                <ChevronsLeft className="h-4 w-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8"
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              
              {/* Page numbers */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <Button
                    key={pageNum}
                    size="icon"
                    variant={currentPage === pageNum ? "default" : "ghost"}
                    className={`h-8 w-8 ${currentPage === pageNum ? "bg-brand-primary hover:bg-brand-primary/90 text-white" : ""}`}
                    onClick={() => goToPage(pageNum)}
                  >
                    {pageNum}
                  </Button>
                );
              })}
              
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8"
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8"
                onClick={() => goToPage(totalPages)}
                disabled={currentPage === totalPages}
              >
                <ChevronsRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      <ApiLogDetailModal
        logId={selectedLogId}
        open={isDetailOpen}
        onOpenChange={setIsDetailOpen}
      />

      <CustomDateRangeDialog
        open={isCustomDateRangeOpen}
        onOpenChange={setIsCustomDateRangeOpen}
        onConfirm={handleCustomDateRangeConfirm}
      />
    </DashboardLayout>
  )
}
